// SidebarAccount — the account row and its menu (spec §3), pure.
//
// The row: an initials avatar, then (expanded) the name over "Settings". It
// opens the account menu: name + email, Settings, Theme (segmented),
// Language (a submenu, the current one named), Sign out. Only what works is
// offered: the theme has Light and Dark (System renders as Light today, so
// Settings leaves it out too), and Sign out shows only when the host can
// sign out.

export { SidebarAccount };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";

type Choice = {
    readonly value: string;
    readonly label: string;
};

type SidebarAccountProps = {
    readonly name: string;
    readonly email: string;
    readonly initials: string;
    readonly expanded: boolean;
    // Settings is the current view.
    readonly current: boolean;
    readonly onSettings: () => void;
    readonly theme: "light" | "dark";
    readonly onTheme: (theme: "light" | "dark") => void;
    readonly languages: readonly Choice[];
    readonly language: string;
    readonly onLanguage: (value: string) => void;
    readonly onSignOut?: () => void;
};

const SidebarAccount = ({
    name,
    email,
    initials,
    expanded,
    current,
    onSettings,
    theme,
    onTheme,
    languages,
    language,
    onLanguage,
    onSignOut,
}: SidebarAccountProps): ReactNode => {
    const { t } = useLingui();
    const items: readonly MenuItem[] = [
        {
            _tag: "label",
            label: (
                <span className="bilda-sidebar__account-head">
                    <span className="bilda-sidebar__account-head-name">
                        {name}
                    </span>
                    {/* No display name: the name is the email already. */}
                    {email === name ? null : <span>{email}</span>}
                </span>
            ),
        },
        { _tag: "separator" },
        {
            _tag: "item",
            label: t`Settings`,
            icon: <Icon name="gear" />,
            onSelect: onSettings,
        },
        {
            _tag: "segmented",
            label: t`Theme`,
            icon: <Icon name={theme === "dark" ? "moon" : "sun"} />,
            options: [
                { value: "light", label: t`Light` },
                { value: "dark", label: t`Dark` },
            ],
            value: theme,
            onChange: (value) => onTheme(value === "dark" ? "dark" : "light"),
        },
        {
            _tag: "submenu",
            label: t`Language`,
            icon: <Icon name="globe" />,
            hint: languages.find((l) => l.value === language)?.label,
            items: languages.map(
                (l): MenuItem => ({
                    _tag: "item",
                    label: l.label,
                    checked: l.value === language,
                    onSelect: () => onLanguage(l.value),
                }),
            ),
        },
        ...(onSignOut === undefined
            ? []
            : [
                  { _tag: "separator" } satisfies MenuItem,
                  {
                      _tag: "item",
                      label: t`Sign out`,
                      icon: <Icon name="sign-out" />,
                      onSelect: onSignOut,
                  } satisfies MenuItem,
              ]),
    ];
    return (
        <Menu
            side={expanded ? "top" : "right"}
            align={expanded ? "start" : "end"}
            items={items}
            trigger={
                <button
                    type="button"
                    className="bilda-sidebar__account"
                    aria-label={t`${name}, account menu`}
                    data-active={current ? "true" : undefined}
                >
                    <span className="bilda-sidebar__avatar" aria-hidden="true">
                        {initials}
                    </span>
                    {expanded ? (
                        <span className="bilda-sidebar__account-text">
                            <span className="bilda-sidebar__account-name">
                                {name}
                            </span>
                            <span className="bilda-sidebar__account-hint">
                                {t`Settings`}
                            </span>
                        </span>
                    ) : null}
                </button>
            }
        />
    );
};
